import type { Editor as TiptapEditor } from "@tiptap/core";
import InlineSVG from "react-inlinesvg";
import { AnchorIds, TourIds, TourSignal } from "../TourTypes";
import { ArrowPosition } from "../StickyNote";
import { hasExtraSpacing } from "../../../lib/tipTapExtensions/removeExtraSpacing";
import { useStickyTourStore } from "../../../stores/stickyTourStore";
import { useSudowriteStore } from "../../../stores/sudowriteStore";
import { PARAGRAPH_SPACING } from "../../../../../src/shared/constants";

/**
 * Teaches existing users (who don't have the 1.5x paragraph-spacing default)
 * about the Paragraph Spacing setting and the Remove Empty Lines toolbar
 * button: gear → paragraph spacing controls → collapse button.
 */
const PARAGRAPH_SPACING_TOUR = {
  id: TourIds.PARAGRAPH_SPACING,
  steps: [
    {
      id: "1",
      stickies: [
        {
          content: (
            <p>
              New <strong>Paragraph Spacing</strong> settings! Click{" "}
              <InlineSVG
                src="/assets/gear-v2.svg"
                aria-label="settings gear"
                className="inline-block align-middle"
                style={{ width: 22, height: 22, marginTop: -3 }}
              />{" "}
              to check them out.
            </p>
          ),
          offsetX: -240,
          offsetY: 45,
          anchorId: AnchorIds.SETTINGS_GEAR,
          arrowPosition: ArrowPosition.TopRight,
          rotation: -2,
        },
      ],
      listeningFor: [TourSignal.SETTINGS_GEAR_CLICKED],
    },
    {
      id: "2",
      stickies: [
        {
          content: <p>Pick how much space you'd like between your paragraphs.</p>,
          offsetX: -280,
          offsetY: 0,
          anchorId: AnchorIds.SETTINGS_PARAGRAPH_SPACING,
          arrowPosition: ArrowPosition.TopRight,
          rotation: 3,
        },
      ],
      listeningFor: [TourSignal.SETTINGS_PANEL_CLOSED],
    },
    {
      id: "3",
      stickies: [
        {
          content: (
            <p>
              Click{" "}
              <InlineSVG
                src="/assets/editor-toolbar/collapse-empty-paragraphs.svg"
                aria-label="remove empty lines"
                className="inline-block align-middle"
                style={{ width: 22, height: 22, marginTop: -3 }}
              />{" "}
              to remove any empty lines.
            </p>
          ),
          offsetX: -105,
          offsetY: 45,
          anchorId: AnchorIds.COLLAPSE_EMPTY_PARAGRAPHS_BUTTON,
          arrowPosition: ArrowPosition.Top,
          rotation: -2,
        },
      ],
      listeningFor: [TourSignal.GLOBAL_CLICK],
    },
  ],
};

const RETRY_INTERVAL_MS = 500;
const MAX_RETRIES = 30;

/**
 * Auto-starts the paragraph spacing tour when a document finishes loading.
 * Only fires for users who don't have the 1.5x paragraph-spacing default
 * (they predate it), haven't seen this onboarding, and opened a document that
 * uses empty paragraphs as spacing. New-user onboarding (needsDemo) owns the
 * session for brand-new accounts, so it's skipped there.
 *
 * The editor can finish syncing before userData loads (or while another
 * tour is cleaning up), so undecidable checks retry briefly instead of
 * silently dropping the tour.
 */
export const maybeStartParagraphSpacingTour = (editor: TiptapEditor | null | undefined) => {
  if (!editor || editor.isDestroyed) return;

  const attempt = (): "retry" | "done" => {
    if (editor.isDestroyed) return "done";

    const { userData, editorOptions } = useSudowriteStore.getState();
    if (!userData?.fetched) return "retry";
    if (userData.data?.needsDemo) return "done";
    if (userData.data?.hasSeenParagraphSpacingOnboarding) return "done";
    if ((editorOptions?.paragraphSpacing || 0) === PARAGRAPH_SPACING.X1_5) return "done";
    if (!hasExtraSpacing(editor.state.doc)) return "done";

    const { phase, startTour } = useStickyTourStore.getState();
    if (phase !== "idle") return "retry";

    startTour(PARAGRAPH_SPACING_TOUR);
    return "done";
  };

  if (attempt() === "done") return;

  let retries = 0;
  const interval = setInterval(() => {
    retries += 1;
    if (attempt() === "done" || retries >= MAX_RETRIES) {
      clearInterval(interval);
    }
  }, RETRY_INTERVAL_MS);
};

export { PARAGRAPH_SPACING_TOUR };
